"use client";

import {
  createIntegration,
  deleteIntegration,
  getAllInstalledIntegrations,
  InstalledIntegration_ALL_GET,
} from "@/api/client/installedintegration";
import {
  buildGoogleCalendarOAuthUrl,
  buildMicrosoftOutlookOAuthUrl,
  buildSalesforceOAuthUrl,
  buildZoomOAuthUrl,
} from "@/lib";
import {
  InstalledIntegration,
  InstalledIntegrationStatus,
  InstalledIntegrationType,
} from "@/types/installedintegration";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ensureParagonAuthenticated } from "@/lib/paragonAuth";
import {
  ACCOUNTS_CRM_GET,
  ACCOUNTS_CRM_GET_OPTIONS,
  CLIENTS_CRM_GET,
  CONTACTS_CRM_GET_OPTIONS,
  GET_USER_CRM_SETTINGS,
  USER_CRM_GET_OPTIONS,
  USER_CRM_UPDATE_SETTINGS,
  USERS_CRM_GET,
} from "@/api";
import { toast } from "sonner";
import { showErrorToast } from "@/lib/errorUtils";
import { useCrmTaskStore, useCalendarIntegrationsStore } from "@/store";
import { CrmType } from "@/types/crm-settings";
import { FeatureFlagName } from "@/types/feature-flag";
import {
  GET_PARAGON_CONFIG,
  getParagonConfigs,
} from "@/api/client/paragon-config";
import {
  GET_CRM_CONFIGURATION,
  getCrmConfigurations,
} from "@/api/client/crm-configuration";
import { ParagonConfig } from "@/types/paragon-config";
import { paragon, SDK_EVENT } from "@useparagon/connect";
import {
  GET_PARAGON_JWT,
  getParagonJwt,
  getParagonSyncStatus,
  PARAGON_SYNC_STATUS_KEY,
} from "@/api/client/paragon";
import { ParagonConfiguration } from "@/types/paragon";
import { useCalendars } from "@/hooks";
import { usePollingGate } from "@/hooks/usePollingGate";
import { usePollingLeader } from "@/hooks/usePollingLeader";
import { useReconnectRefetch } from "@/hooks/useReconnectRefetch";
import { CALENDARS_GET, CalendarResponse } from "@/api/client/calendar";
import { useWebSocketSubscription } from "@/context/WebSocketProvider";
import { CloudEventType } from "@/types/cloud-events";

export enum Integrations {
  GOOGLE = "google",
  MICROSOFT = "microsoft",
  ZOOM = "zoom",
  SALESFORCE = "salesforce",
}

export type ParagonAuthState = "idle" | "loading" | "ready" | "error";
export type ParagonConfigsState = "idle" | "loading" | "ready" | "error";

// Backend issues 1-hour Paragon JWTs. Refetch before expiry so
// paragon.authenticate isn't called with a stale token.
const PARAGON_JWT_STALE_MS = 50 * 60 * 1000;
const PARAGON_JWT_GC_MS = 55 * 60 * 1000;

// The best-effort Paragon SDK uninstall must never block the authoritative
// backend disconnect. The try/catch around these calls handles rejections, but
// a hung SDK promise (one that never settles) would stall the disconnect chain
// indefinitely, so each uninstall is bounded by this timeout and then allowed
// to proceed to the backend removal.
const PARAGON_UNINSTALL_TIMEOUT_MS = 5000;

const withUninstallTimeout = (promise: Promise<unknown>): Promise<unknown> =>
  Promise.race([
    promise,
    new Promise<void>((resolve) =>
      setTimeout(resolve, PARAGON_UNINSTALL_TIMEOUT_MS),
    ),
  ]);

// A successful OAuth also closes the popup, after which the Paragon SDK polls
// for the credential (CREDENTIAL_POLL_INTERVAL = 3s) before firing onSuccess.
// The popup-close watcher can't distinguish that from a real cancel, so on
// close it waits this grace period — long enough to cover a poll cycle — before
// treating the close as a cancel. onSuccess/onError stop the watcher first,
// cancelling the timer, so a normal completion never clears "Connecting..."
// early (which otherwise left the pill blank for a few seconds).
const OAUTH_POPUP_CANCEL_GRACE_MS = 4000;

// installIntegration opens its popup synchronously, so the watcher's window.open
// wrapper only has to survive the current tick. installFlow.start advances its
// stages asynchronously first, so its popup lands a beat later — inside Chrome's
// transient-activation window, but after tick 0. Keep the wrapper up that long so
// the popup is still captured.
const OAUTH_POPUP_CAPTURE_WINDOW_MS = 5000;

export interface IntegrationsPage {
  connect: (platform: Integrations) => void;
  disconnect: (id: string, successCallback?: () => void) => void;
  installedIntegrations?: InstalledIntegration[];
  isFetched: boolean;
  isFetching: boolean;
  isCalendarConnected: boolean;
  isIntegrationsLoading: boolean;
  // Integration finding
  google?: InstalledIntegration[];
  microsoft?: InstalledIntegration[];
  calendars?: CalendarResponse[] | null;
  zoom?: InstalledIntegration;
  salesforce?: InstalledIntegration;
  wealthbox?: InstalledIntegration;
  redtail?: InstalledIntegration;
  laylah?: InstalledIntegration;
  /** True once the firm admin has saved the Laylah CrmConfiguration. */
  isLaylahFirmConfigured: boolean;
  isCrmConfigurationsLoading: boolean;
  isCrmConfigurationsError: boolean;
  refetchCrmConfigurations: () => void;
  paragonSalesforceEnabled: boolean;
  isParagonJwtLoading: boolean;
  isParagonUserSet: boolean;
  paragonAuthState: ParagonAuthState;
  paragonConfigsState: ParagonConfigsState;
  retryParagonAuth: () => void;
  paragonConfigs: ParagonConfig[] | undefined;
  paragonSalesforce?: InstalledIntegration;
  paragonXlr8?: InstalledIntegration;
  paragonPractifi?: InstalledIntegration;
  paragonBlackDiamondElements?: InstalledIntegration;
  paragonFsc?: InstalledIntegration;
  paragonMicrosoftDynamics?: InstalledIntegration;
  paragonZoho?: InstalledIntegration;
  paragonCloven?: InstalledIntegration;
  paragonMaximizer?: InstalledIntegration;
  paragonEquisoft?: InstalledIntegration;
  paragonGoogle?: InstalledIntegration[];
  paragonMicrosoft?: InstalledIntegration[];
  paragonGoogleEmailConfigured: boolean;
  paragonMicrosoftEmailConfigured: boolean;
  paragonGoogleAccounts: Array<{
    id: string;
    email: string | null;
    status?: InstalledIntegrationStatus;
  }>;
  paragonMicrosoftAccounts: Array<{
    id: string;
    email: string | null;
    status?: InstalledIntegrationStatus;
  }>;
  handleParagonEmailDisconnect: (id: string) => Promise<void>;
  paragonSyncStatus?: Record<string, boolean>;
  handleParagonMicrosoftDynamicsIntegrationInstall: () => Promise<void>;
  handleParagonZohoIntegrationInstall: () => Promise<void>;
  handleParagonClovenIntegrationInstall: () => Promise<void>;
  handleParagonMaximizerIntegrationInstall: () => Promise<void>;
  handleParagonEquisoftIntegrationInstall: () => Promise<void>;
  handleParagonGoogleIntegrationInstall: () => Promise<void>;
  handleParagonMicrosoftIntegrationInstall: () => Promise<void>;
  handleParagonSalesforceSkinIntegrationInstall: (
    integrationType:
      | InstalledIntegrationType.PARAGON_SALESFORCE
      | InstalledIntegrationType.PARAGON_XLR8
      | InstalledIntegrationType.PARAGON_PRACTIFI
      | InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS
      | InstalledIntegrationType.PARAGON_FSC,
  ) => Promise<void>;
  isAnyCalendarConnected: boolean;
  // Integration states
  integrationStates: {
    teams: {
      isConnected: boolean;
      isEnabled: boolean;
      onConnect?: () => void;
      onDisconnect?: () => void;
    };
    meet: {
      isConnected: boolean;
      isEnabled: boolean;
      onConnect?: () => void;
      onDisconnect?: () => void;
    };
    zoom: {
      isConnected: boolean;
      isEnabled: boolean;
      enabledStatusText?: string;
      onConnect?: () => void;
      onDisconnect?: () => void;
    };
  };
  // CRM logic
  onConnectToCrm: (type: CrmType) => void;
  disconnectOtherCrms: (type: CrmType | null) => void;
  srmsList: (InstalledIntegration | undefined)[];
  // Modal state
  integrationToDisconnect?: string;
  setIntegrationToDisconnect: (id: string | undefined) => void;
  handleBeforeDisconnect: (id: string) => Promise<void>;
  activeCrm: CrmType | null;
  setActiveCrm: (crm: CrmType | null) => void;
  warningModal: CrmType | null;
  setWarningModal: (crm: CrmType | null) => void;
  connectingIntegrations: Set<string>;
  disconnectingIds: Set<string>;
  setConnectingIntegrations: (
    value: Set<string> | ((prev: Set<string>) => Set<string>),
  ) => void;
}

interface UseIntegrationsPageProps {
  userId?: string;
  getFeatureFlag: (flag: FeatureFlagName) => { value: string };
  withRefetchInterval?: boolean;
}

interface DisconnectParams {
  id: string;
  successCallback?: () => void;
}

export const useIntegrationsPage = ({
  userId,
  getFeatureFlag,
  withRefetchInterval = false,
}: UseIntegrationsPageProps): IntegrationsPage => {
  const queryClient = useQueryClient();

  // Calendar connect, disconnect, and token-invalidation each emit a distinct
  // `com.focal.calendar.*` event on the per-user `calendar` room. The backend
  // create/remove paths emit CONNECTED/DISCONNECTED (not UPDATED), so listening
  // for CALENDAR_UPDATED alone would miss connect/disconnect over the socket.
  // Invalidate both the calendars list and the installed-integrations cache so
  // the integrations page reflects the change live without polling.
  const handleCalendarMessage = useCallback(
    (event: { type: string }) => {
      if (
        event.type === CloudEventType.CALENDAR_CONNECTED ||
        event.type === CloudEventType.CALENDAR_DISCONNECTED ||
        event.type === CloudEventType.CALENDAR_UPDATED
      ) {
        queryClient.invalidateQueries({
          queryKey: [CALENDARS_GET],
          exact: true,
        });
        queryClient.invalidateQueries({
          queryKey: [InstalledIntegration_ALL_GET],
          exact: true,
        });
      }
    },
    [queryClient],
  );

  // Keep the calendar subscription so calendar status updates live over the
  // socket; the returned connection flag is no longer used to drive polling
  // (the gate below owns that).
  useWebSocketSubscription("calendar", undefined, handleCalendarMessage);

  // CRM credential-health / token-expiry changes (e.g. NEEDS_RECONNECT) arrive
  // on the per-user `integration` room as INTEGRATION_STATUS_CHANGED. Invalidate
  // the installed-integrations and Paragon sync-status caches so those surface
  // live now that the 30s paragonSyncStatus poll is gated off while healthy.
  const handleIntegrationMessage = useCallback(
    (event: { type: string }) => {
      if (event.type === CloudEventType.INTEGRATION_STATUS_CHANGED) {
        queryClient.invalidateQueries({
          queryKey: [InstalledIntegration_ALL_GET],
          exact: true,
        });
        queryClient.invalidateQueries({
          queryKey: [PARAGON_SYNC_STATUS_KEY],
          exact: true,
        });
      }
    },
    [queryClient],
  );

  useWebSocketSubscription("integration", undefined, handleIntegrationMessage);

  // This hook is mounted by many components at once (every `useCrm` consumer),
  // so only the leader instance polls and reconciles on reconnect. The others
  // share its results through the query cache.
  const isPollingLeader = usePollingLeader("integrations-page");

  // Polling is a connection-state fallback only. While the socket is healthy
  // `usePollingGate` returns `false` (no interval refetch); while unhealthy it
  // returns the fallback interval. `withRefetchInterval` only raises that
  // fallback interval (10s) — it never forces polling while healthy.
  const integrationsGateInterval = usePollingGate(
    withRefetchInterval ? 10000 : 5000,
  );
  const integrationsPollInterval = isPollingLeader
    ? integrationsGateInterval
    : false;

  // On reconnect, reconcile integration + calendar status missed during the
  // outage with a one-shot refetch (events are dropped while disconnected).
  // Covers calendar connect/disconnect and INTEGRATION_STATUS_CHANGED, so the
  // Paragon sync-status cache is reconciled alongside the installed list.
  useReconnectRefetch(() => {
    if (!isPollingLeader) return;
    queryClient.invalidateQueries({
      queryKey: [InstalledIntegration_ALL_GET],
      exact: true,
    });
    queryClient.invalidateQueries({
      queryKey: [CALENDARS_GET],
      exact: true,
    });
    queryClient.invalidateQueries({
      queryKey: [PARAGON_SYNC_STATUS_KEY],
      exact: true,
    });
  });

  const {
    data: installedIntegrations,
    isFetched,
    isFetching,
  } = useQuery<InstalledIntegration[]>({
    queryKey: [InstalledIntegration_ALL_GET],
    queryFn: () => getAllInstalledIntegrations(),
    enabled: true,
    refetchInterval: integrationsPollInterval,
  });

  const { clearCrmTasks } = useCrmTaskStore();

  // Modal state
  const [integrationToDisconnect, setIntegrationToDisconnect] =
    useState<string>();
  const [activeCrm, setActiveCrm] = useState<CrmType | null>(null);
  const [warningModal, setWarningModal] = useState<CrmType | null>(null);
  const [paragonUser, setParagonUser] = useState<any | undefined>(undefined);
  const [connectingIntegrations, setConnectingIntegrations] = useState<
    Set<string>
  >(new Set());
  // Row ids being disconnected — drives the pill spinner and disables its
  // remove button so double-clicks can't error on an already-deleted row.
  const [disconnectingIds, setDisconnectingIds] = useState<Set<string>>(
    new Set(),
  );
  // Synchronous mirror of disconnectingIds: a setState updater may not run
  // synchronously, so the ref is the source of truth for the re-entry guard.
  const disconnectingIdsRef = useRef<Set<string>>(new Set());
  // Per-provider connect attempt counter. State is keyed by provider, so a
  // flow only clears "Connecting..." if it's still the latest attempt — stops a
  // superseded attempt's late callback from wiping a newer attempt's spinner.
  const connectAttemptRef = useRef<Record<string, number>>({});

  // Load calendar IDs into zustand store when integrations finish loading
  const { loadCalendarIds } = useCalendarIntegrationsStore();
  useEffect(() => {
    if (isFetched && installedIntegrations) {
      const calendarIntegrations = installedIntegrations.filter((i) =>
        [
          InstalledIntegrationType.GOOGLECALENDAR,
          InstalledIntegrationType.MICROSOFTCALENDAR,
        ].includes(i.integration),
      );
      loadCalendarIds(calendarIntegrations);
    }
  }, [isFetched, installedIntegrations, loadCalendarIds]);
  const paragonSalesforceEnabled =
    getFeatureFlag(FeatureFlagName.PARAGON_SALESFORCE).value === "on";

  const { mutate: disconnect } = useMutation({
    mutationFn: ({ id }: DisconnectParams) => deleteIntegration(id),
    onSuccess: async (data, { successCallback }) => {
      toast("Integration was disconnected");

      if (successCallback) {
        successCallback();
      }

      await queryClient.invalidateQueries({
        queryKey: [InstalledIntegration_ALL_GET],
      });
      queryClient.invalidateQueries({
        queryKey: [CALENDARS_GET],
      });
      queryClient.removeQueries({
        queryKey: [GET_USER_CRM_SETTINGS],
      });
      queryClient.removeQueries({
        queryKey: [CONTACTS_CRM_GET_OPTIONS],
      });
      queryClient.removeQueries({
        queryKey: [ACCOUNTS_CRM_GET_OPTIONS],
      });
      queryClient.removeQueries({
        queryKey: [USER_CRM_GET_OPTIONS],
      });
      queryClient.removeQueries({
        queryKey: [USER_CRM_UPDATE_SETTINGS],
      });
      queryClient.removeQueries({
        queryKey: [CLIENTS_CRM_GET],
      });
      queryClient.removeQueries({
        queryKey: [ACCOUNTS_CRM_GET],
      });
      queryClient.removeQueries({
        queryKey: [USERS_CRM_GET],
      });

      clearCrmTasks();
    },
    onError: (error) => {
      showErrorToast(error, "integrations");
    },
    onSettled: (_data, _error, { id }) => {
      // Clear the disconnecting flag once the mutation resolves. On success the
      // pill is already gone (onSuccess awaited invalidation); on error it stays
      // and re-enables for a retry. No-op for CRM rows (never in the set).
      if (!disconnectingIdsRef.current.has(id)) return;
      disconnectingIdsRef.current.delete(id);
      setDisconnectingIds(new Set(disconnectingIdsRef.current));
    },
  });

  const {
    data: paragonConfigs,
    isFetching: isParagonConfigsFetching,
    isError: isParagonConfigsError,
    refetch: refetchParagonConfigs,
  } = useQuery({
    queryKey: [GET_PARAGON_CONFIG],
    queryFn: () => getParagonConfigs(),
    // Firm-level configs are edited in firm-admin (a separate app), so React
    // Query can't observe the writes. Refetch on every mount and on tab focus
    // so the user-side Connect buttons unstick after the firm admin configures
    // an integration without requiring a full page refresh.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: 1,
    retryDelay: 300,
  });

  // Firm-level configs for non-Paragon integrations (currently Laylah). Same
  // aggressive refetch policy as paragonConfigs and for the same reason: the
  // firm admin edits these in a separate app, so the user-side Connect button
  // must unstick without a full page refresh. Only fetched when the Laylah
  // tile (its sole consumer) can render.
  const {
    data: crmConfigurations,
    isLoading: isCrmConfigurationsLoading,
    isError: isCrmConfigurationsError,
    refetch: refetchCrmConfigurations,
  } = useQuery({
    queryKey: [GET_CRM_CONFIGURATION],
    queryFn: () => getCrmConfigurations(),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: 1,
    retryDelay: 300,
  });

  // The backend rejects the Laylah OAuth callback until the firm admin has
  // saved a CrmConfiguration, so the tile is gated on it (mirroring the
  // Paragon tiles' ParagonConfig gate).
  const isLaylahFirmConfigured =
    crmConfigurations != undefined &&
    crmConfigurations.some(
      (config) => config.integration === InstalledIntegrationType.LAYLAH,
    );

  const hasParagonProjectId = !!process.env.NEXT_PUBLIC_PARAGON_PROJECTID;

  // Query for Paragon sync status to show sync errors on integration cards.
  // No dedicated socket event exists for Paragon sync today, so the gate falls
  // back to the existing 30s poll only while the socket is unhealthy; if the
  // backend adds a Paragon event later, the gate already disables polling.
  const paragonSyncGateInterval = usePollingGate(30000);
  const { data: paragonSyncStatus } = useQuery({
    queryKey: [PARAGON_SYNC_STATUS_KEY],
    queryFn: getParagonSyncStatus,
    staleTime: 30000, // Match polling interval for consistency
    refetchInterval: isPollingLeader ? paragonSyncGateInterval : false,
    refetchOnWindowFocus: false, // Polling already handles freshness
    retry: 1,
  });

  // Query for Paragon JWT
  const {
    data: paragonJwt,
    isLoading: isParagonJwtLoading,
    isFetching: isParagonJwtFetching,
    isError: isParagonJwtError,
    refetch: refetchParagonJwt,
  } = useQuery({
    queryKey: [GET_PARAGON_JWT],
    queryFn: getParagonJwt,
    enabled: hasParagonProjectId,
    staleTime: PARAGON_JWT_STALE_MS,
    gcTime: PARAGON_JWT_GC_MS,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: 300,
  });

  // Track which JWT string succeeded / failed paragon.authenticate(). Comparing
  // against the current paragonJwt lets us derive auth state without calling
  // setState synchronously in the effect body.
  const [authedJwt, setAuthedJwt] = useState<string | null>(null);
  const [failedJwt, setFailedJwt] = useState<string | null>(null);
  const [paragonRetryCount, setParagonRetryCount] = useState(0);

  useEffect(() => {
    if (
      !paragonJwt ||
      !process.env.NEXT_PUBLIC_PARAGON_PROJECTID ||
      isParagonJwtFetching ||
      isParagonJwtError
    ) {
      return;
    }
    if (authedJwt === paragonJwt || failedJwt === paragonJwt) {
      // Already resolved for this JWT — don't re-run.
      return;
    }

    let cancelled = false;
    const jwt = paragonJwt;

    const initializeParagon = async () => {
      try {
        await ensureParagonAuthenticated(
          process.env.NEXT_PUBLIC_PARAGON_PROJECTID!,
          jwt,
        );
        if (cancelled) return;
        setAuthedJwt(jwt);
        setParagonUser(paragon.getUser());
      } catch (error) {
        if (cancelled) return;
        console.error("Paragon authentication error:", error);
        setFailedJwt(jwt);
        setParagonUser(undefined);
      }
    };

    void initializeParagon();
    return () => {
      cancelled = true;
    };
  }, [
    paragonJwt,
    isParagonJwtFetching,
    isParagonJwtError,
    authedJwt,
    failedJwt,
    paragonRetryCount,
  ]);

  const paragonAuthState = useMemo<ParagonAuthState>(() => {
    // The project ID env var isn't configured — the JWT query is disabled, so
    // we'd otherwise sit in "loading" forever. Surface as an error so the UI
    // shows a recovery affordance instead of a permanent spinner.
    if (!hasParagonProjectId) return "error";
    if (isParagonJwtError) return "error";
    if (paragonJwt && failedJwt === paragonJwt) return "error";
    if (paragonJwt && authedJwt === paragonJwt) return "ready";
    return "loading";
  }, [
    authedJwt,
    failedJwt,
    hasParagonProjectId,
    isParagonJwtError,
    paragonJwt,
  ]);

  const paragonConfigsState = useMemo<ParagonConfigsState>(() => {
    if (isParagonConfigsError) return "error";
    if (paragonConfigs !== undefined) return "ready";
    if (isParagonConfigsFetching) return "loading";
    return "loading";
  }, [isParagonConfigsError, isParagonConfigsFetching, paragonConfigs]);

  const retryParagonAuth = useCallback(() => {
    setFailedJwt(null);
    if (isParagonJwtError) {
      void refetchParagonJwt();
    }
    if (isParagonConfigsError) {
      void refetchParagonConfigs();
    }
    setParagonRetryCount((n) => n + 1);
  }, [
    isParagonJwtError,
    isParagonConfigsError,
    refetchParagonJwt,
    refetchParagonConfigs,
  ]);

  // Clear connecting state when the Paragon portal is closed (including dismissals)
  useEffect(() => {
    if (!paragonUser) return;

    const ALL_PARAGON_CONNECTING_KEYS = [
      InstalledIntegrationType.PARAGON_ZOHO,
      InstalledIntegrationType.PARAGON_CLOVEN,
      InstalledIntegrationType.PARAGON_MAXIMIZER,
      InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
      InstalledIntegrationType.PARAGON_SALESFORCE,
      InstalledIntegrationType.PARAGON_XLR8,
      InstalledIntegrationType.PARAGON_PRACTIFI,
      InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS,
      InstalledIntegrationType.PARAGON_FSC,
      InstalledIntegrationType.PARAGON_EQUISOFT,
      InstalledIntegrationType.PARAGON_GOOGLE,
      InstalledIntegrationType.PARAGON_MICROSOFT,
    ];

    const PARAGON_INTEGRATION_KEY_MAP: Record<string, string[]> = {
      salesforce: [
        InstalledIntegrationType.PARAGON_SALESFORCE,
        InstalledIntegrationType.PARAGON_XLR8,
        InstalledIntegrationType.PARAGON_PRACTIFI,
        InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS,
        InstalledIntegrationType.PARAGON_FSC,
      ],
      microsoftDynamics: [InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS],
      zohocrm: [
        InstalledIntegrationType.PARAGON_ZOHO,
        InstalledIntegrationType.PARAGON_CLOVEN,
      ],
      maximizer: [InstalledIntegrationType.PARAGON_MAXIMIZER],
      "custom.equisoft": [InstalledIntegrationType.PARAGON_EQUISOFT],
      gmail: [InstalledIntegrationType.PARAGON_GOOGLE],
      outlook: [InstalledIntegrationType.PARAGON_MICROSOFT],
    };

    const handlePortalClose = (
      event: { integrationType?: string },
      user?: unknown,
    ) => {
      // Portal close fires after install/uninstall — refresh paragonUser so
      // integration metadata (providerData, credentialId) is current. Without
      // this the SDK snapshot stays at first-auth-time and freshly-installed
      // integrations report no providerData → email pill stuck on "Connected".
      if (user) {
        setParagonUser(user);
      } else {
        setParagonUser(paragon.getUser());
      }

      // Only clear CRM keys here. Email owns its connecting lifecycle (cleared
      // by onSuccess/onError or the popup-closed watcher), and ON_PORTAL_CLOSE
      // fires a beat late — a prior attempt's close would wipe a fresh
      // "Connecting..." from a newer attempt.
      const keysToRemove = (
        (event.integrationType &&
          PARAGON_INTEGRATION_KEY_MAP[event.integrationType]) ||
        ALL_PARAGON_CONNECTING_KEYS
      ).filter(
        (key) =>
          key !== InstalledIntegrationType.PARAGON_GOOGLE &&
          key !== InstalledIntegrationType.PARAGON_MICROSOFT,
      );

      setConnectingIntegrations((prev) => {
        const next = new Set(prev);
        for (const key of keysToRemove) {
          next.delete(key);
        }
        return next;
      });
    };

    const unsubscribe = paragon.subscribe(
      SDK_EVENT.ON_PORTAL_CLOSE,
      handlePortalClose as Parameters<typeof paragon.subscribe>[1],
    );

    return () => {
      unsubscribe();
    };
  }, [paragonUser]);

  const getParagonFirmConfigurationId = useCallback(
    (
      integrationType:
        | InstalledIntegrationType.PARAGON_SALESFORCE
        | InstalledIntegrationType.PARAGON_XLR8
        | InstalledIntegrationType.PARAGON_PRACTIFI
        | InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS
        | InstalledIntegrationType.PARAGON_FSC
        | InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS
        | InstalledIntegrationType.PARAGON_ZOHO
        | InstalledIntegrationType.PARAGON_CLOVEN
        | InstalledIntegrationType.PARAGON_MAXIMIZER
        | InstalledIntegrationType.PARAGON_EQUISOFT,
    ): string | null => {
      const config: ParagonConfig | undefined = paragonConfigs?.find(
        (c) => c.integration === integrationType,
      );
      const firmConfiguration: ParagonConfiguration | undefined =
        config?.configuration?.allConfigurations?.find(
          (c: ParagonConfiguration) =>
            c.connectCredentialId === config.connectionId,
        );
      return firmConfiguration?.id ?? null;
    },
    [paragonConfigs],
  );

  const handleParagonZohoIntegrationInstall: () => Promise<void> = async () => {
    if (!paragonJwt || !paragonUser) {
      console.error("Paragon not authenticated yet");
      return;
    }
    setConnectingIntegrations((prev) =>
      new Set(prev).add(InstalledIntegrationType.PARAGON_ZOHO),
    );
    try {
      const zohoConfig = paragonConfigs?.find(
        (config) =>
          config.integration === InstalledIntegrationType.PARAGON_ZOHO,
      );
      const firmConfig = zohoConfig?.configuration.allConfigurations?.find(
        (c: ParagonConfiguration) =>
          c.connectCredentialId === zohoConfig.connectionId,
      );

      if (!firmConfig) {
        console.error("Zoho configuration not found");
        setConnectingIntegrations((prev) => {
          const next = new Set(prev);
          next.delete(InstalledIntegrationType.PARAGON_ZOHO);
          return next;
        });
        return;
      }

      // Check if user has a user-level installed integration
      const hasUserLevelIntegration = paragonZoho?.externalId != undefined;

      // If Paragon SDK shows firm-level connection but no user-level integration exists,
      // we need to uninstall the firm-level connection from SDK first to prevent
      // the modal from showing as "Connected"
      if (
        !hasUserLevelIntegration &&
        paragonUser.integrations?.zohocrm?.credentialId
      ) {
        // Change connected to be false and not use ANY connection
      }

      paragon.installIntegration("zohocrm", {
        showPortalAfterInstall: false,
        allowMultipleCredentials: true,
        async onSuccess(event) {
          try {
            console.log("Paragon event", event);
            await createIntegration({
              integration: InstalledIntegrationType.PARAGON_ZOHO,
              externalId: event.credentialId ?? "",
            });

            await queryClient.invalidateQueries({
              queryKey: [InstalledIntegration_ALL_GET],
            });
            toast.success("Zoho CRM has been successfully connected.");
          } catch (error) {
            console.error("Failed to save Zoho integration:", error);
            showErrorToast(error, "integrations");
            // Rollback the Paragon credential to prevent state mismatch
            try {
              await paragon.uninstallIntegration("zohocrm", {
                selectedCredentialId: event.credentialId,
              });
            } catch (rollbackError) {
              console.warn(
                "Failed to rollback Zoho Paragon credential:",
                rollbackError,
              );
            }
          } finally {
            setConnectingIntegrations((prev) => {
              const next = new Set(prev);
              next.delete(InstalledIntegrationType.PARAGON_ZOHO);
              return next;
            });
          }
        },
        onError(error) {
          console.error("zoho connect error", error);
          showErrorToast(error, "integrations");
          setConnectingIntegrations((prev) => {
            const next = new Set(prev);
            next.delete(InstalledIntegrationType.PARAGON_ZOHO);
            return next;
          });
        },
      });
    } catch (error) {
      console.error("Paragon Zoho installation error:", error);
      setConnectingIntegrations((prev) => {
        const next = new Set(prev);
        next.delete(InstalledIntegrationType.PARAGON_ZOHO);
        return next;
      });
    }
  };

  const handleParagonClovenIntegrationInstall: () => Promise<void> =
    async () => {
      if (!paragonJwt || !paragonUser) {
        console.error("Paragon not authenticated yet");
        return;
      }
      setConnectingIntegrations((prev) =>
        new Set(prev).add(InstalledIntegrationType.PARAGON_CLOVEN),
      );
      try {
        const clovenConfig = paragonConfigs?.find(
          (config) =>
            config.integration === InstalledIntegrationType.PARAGON_CLOVEN,
        );
        const firmConfig = clovenConfig?.configuration.allConfigurations?.find(
          (c: ParagonConfiguration) =>
            c.connectCredentialId === clovenConfig.connectionId,
        );

        if (!firmConfig) {
          console.error("Cloven configuration not found");
          setConnectingIntegrations((prev) => {
            const next = new Set(prev);
            next.delete(InstalledIntegrationType.PARAGON_CLOVEN);
            return next;
          });
          return;
        }

        // Check if user has a user-level installed integration
        const hasUserLevelIntegration = paragonCloven?.externalId != undefined;

        // If Paragon SDK shows firm-level connection but no user-level integration exists,
        // we need to uninstall the firm-level connection from SDK first to prevent
        // the modal from showing as "Connected"
        if (
          !hasUserLevelIntegration &&
          paragonUser.integrations?.zohocrm?.credentialId
        ) {
          // Change connected to be false and not use ANY connection
        }

        paragon.installIntegration("zohocrm", {
          showPortalAfterInstall: false,
          allowMultipleCredentials: true,
          async onSuccess(event) {
            try {
              console.log("Paragon event", event);
              await createIntegration({
                integration: InstalledIntegrationType.PARAGON_CLOVEN,
                externalId: event.credentialId ?? "",
              });

              await queryClient.invalidateQueries({
                queryKey: [InstalledIntegration_ALL_GET],
              });
              toast.success("Cloven has been successfully connected.");
            } catch (error) {
              console.error("Failed to save Cloven integration:", error);
              showErrorToast(error, "integrations");
              // Rollback the Paragon credential to prevent state mismatch
              try {
                await paragon.uninstallIntegration("zohocrm", {
                  selectedCredentialId: event.credentialId,
                });
              } catch (rollbackError) {
                console.warn(
                  "Failed to rollback Cloven Paragon credential:",
                  rollbackError,
                );
              }
            } finally {
              setConnectingIntegrations((prev) => {
                const next = new Set(prev);
                next.delete(InstalledIntegrationType.PARAGON_CLOVEN);
                return next;
              });
            }
          },
          onError(error) {
            console.error("cloven connect error", error);
            showErrorToast(error, "integrations");
            setConnectingIntegrations((prev) => {
              const next = new Set(prev);
              next.delete(InstalledIntegrationType.PARAGON_CLOVEN);
              return next;
            });
          },
        });
      } catch (error) {
        console.error("Paragon Cloven installation error:", error);
        setConnectingIntegrations((prev) => {
          const next = new Set(prev);
          next.delete(InstalledIntegrationType.PARAGON_CLOVEN);
          return next;
        });
      }
    };

  const handleParagonMaximizerIntegrationInstall: () => Promise<void> =
    async () => {
      if (!paragonJwt || !paragonUser) {
        console.error("Paragon not authenticated yet");
        return;
      }
      setConnectingIntegrations((prev) =>
        new Set(prev).add(InstalledIntegrationType.PARAGON_MAXIMIZER),
      );
      let stopCancelWatch = () => {};
      const clearConnecting = () => {
        stopCancelWatch();
        setConnectingIntegrations((prev) => {
          const next = new Set(prev);
          next.delete(InstalledIntegrationType.PARAGON_MAXIMIZER);
          return next;
        });
      };
      try {
        const maximizerConfig = paragonConfigs?.find(
          (config) =>
            config.integration === InstalledIntegrationType.PARAGON_MAXIMIZER,
        );
        const firmConfig =
          maximizerConfig?.configuration.allConfigurations?.find(
            (c: ParagonConfiguration) =>
              c.connectCredentialId === maximizerConfig.connectionId,
          );

        if (!firmConfig) {
          console.error("Maximizer configuration not found");
          clearConnecting();
          return;
        }

        // Firm-level defaults from firm-admin, read from firmConfig.sharedSettings
        // to match Salesforce / Equisoft / Dynamics.
        const sharedSettings =
          (firmConfig?.sharedSettings as Record<string, unknown> | undefined) ??
          {};
        const hasFirmSettings = Object.keys(sharedSettings).length > 0;

        const handleInstallSuccess = async (credentialId: string) => {
          try {
            await createIntegration({
              integration: InstalledIntegrationType.PARAGON_MAXIMIZER,
              externalId: credentialId,
            });

            if (hasFirmSettings) {
              paragon.updateIntegrationUserSettings("custom.maximizer", {
                ...sharedSettings,
              });
            }

            await queryClient.invalidateQueries({
              queryKey: [InstalledIntegration_ALL_GET],
            });
            toast.success("Maximizer CRM has been successfully connected.");
          } catch (error) {
            console.error("Failed to save Maximizer integration:", error);
            toast.error(
              "Connected to Maximizer but failed to save. Please try again.",
            );
            try {
              await paragon.uninstallIntegration("custom.maximizer", {
                selectedCredentialId: credentialId,
              });
            } catch (rollbackError) {
              console.warn(
                "Failed to rollback Maximizer Paragon credential:",
                rollbackError,
              );
            }
          } finally {
            clearConnecting();
          }
        };

        const handleInstallError = (error: unknown) => {
          console.error("maximizer connect error", error);
          showErrorToast(error, "integrations");
          clearConnecting();
        };

        // Headless install: Maximizer resolves the database from the
        // authenticated session, so there is nothing for the advisor to fill in.
        // Auto-advance Paragon's install stages so the Connect Portal dialog
        // never renders and the OAuth popup opens straight from the click —
        // matching every other Paragon integration.
        paragon.setHeadless(true);
        // Nothing watches the popup: the SDK has no close detection, and the
        // abort installFlow.cancel() raises is swallowed rather than surfaced to
        // onError. Without this, dismissing the popup leaves "Connecting..." and
        // headless mode stuck until oauthTimeout fires two minutes later.
        stopCancelWatch = watchForOAuthPopupCancel(() => {
          void paragon.installFlow.cancel().catch((error) => {
            console.warn("Failed to cancel Maximizer install flow:", error);
          });
          paragon.setHeadless(false);
          clearConnecting();
        }, OAUTH_POPUP_CAPTURE_WINDOW_MS);
        paragon.installFlow.start("custom.maximizer", {
          allowMultipleCredentials: true,
          oauthTimeout: 120000,
          onNext: (next) => {
            if (next.done) return;
            if (next.stage === "accountType") {
              paragon.installFlow.setAccountType("default");
              return;
            }
            if (next.stage === "preOptions") {
              paragon.installFlow.setPreOptions({});
              return;
            }
            if (next.stage === "postOptions") {
              paragon.installFlow.setPostOptions({});
              return;
            }
          },
          onComplete: async (event) => {
            // Stop the poll so it can't flip back to "Add" mid-persist.
            stopCancelWatch();
            paragon.setHeadless(false);
            await handleInstallSuccess(event.credentialId ?? "");
          },
          onError: (error) => {
            paragon.setHeadless(false);
            handleInstallError(error);
          },
        });
      } catch (error) {
        paragon.setHeadless(false);
        console.error("Paragon Maximizer installation error:", error);
        clearConnecting();
      }
    };

  const handleParagonEquisoftIntegrationInstall: () => Promise<void> =
    async () => {
      if (!paragonJwt || !paragonUser) {
        console.error("Paragon not authenticated yet");
        return;
      }
      setConnectingIntegrations((prev) =>
        new Set(prev).add(InstalledIntegrationType.PARAGON_EQUISOFT),
      );
      try {
        const equisoftConfig = paragonConfigs?.find(
          (config) =>
            config.integration === InstalledIntegrationType.PARAGON_EQUISOFT,
        );
        const firmConfig =
          equisoftConfig?.configuration.allConfigurations?.find(
            (c: ParagonConfiguration) =>
              c.connectCredentialId === equisoftConfig.connectionId,
          );

        if (!firmConfig) {
          console.error("Equisoft configuration not found");
          setConnectingIntegrations((prev) => {
            const next = new Set(prev);
            next.delete(InstalledIntegrationType.PARAGON_EQUISOFT);
            return next;
          });
          return;
        }

        const hasUserLevelIntegration =
          paragonEquisoft?.externalId != undefined;

        if (
          !hasUserLevelIntegration &&
          paragonUser.integrations?.["custom.equisoft"]?.credentialId
        ) {
          // Change connected to be false and not use ANY connection
        }

        paragon.installIntegration("custom.equisoft", {
          showPortalAfterInstall: false,
          allowMultipleCredentials: true,
          async onSuccess(event) {
            try {
              console.log("Paragon event", event);
              await createIntegration({
                integration: InstalledIntegrationType.PARAGON_EQUISOFT,
                externalId: event.credentialId ?? "",
              });
              /*
               * Copy firm-level workflow settings onto the user-level Paragon credential.
               * Future admin changes will not propagate to existing users (mirrors Salesforce behavior).
               */
              paragon.updateIntegrationUserSettings("custom.equisoft", {
                ...firmConfig?.sharedSettings,
              });

              await queryClient.invalidateQueries({
                queryKey: [InstalledIntegration_ALL_GET],
              });
              toast.success("Equisoft has been successfully connected.");
            } catch (error) {
              console.error("Failed to save Equisoft integration:", error);
              showErrorToast(error, "integrations");
              try {
                await paragon.uninstallIntegration("custom.equisoft", {
                  selectedCredentialId: event.credentialId,
                });
              } catch (rollbackError) {
                console.warn(
                  "Failed to rollback Equisoft Paragon credential:",
                  rollbackError,
                );
              }
            } finally {
              setConnectingIntegrations((prev) => {
                const next = new Set(prev);
                next.delete(InstalledIntegrationType.PARAGON_EQUISOFT);
                return next;
              });
            }
          },
          onError(error) {
            console.error("equisoft connect error", error);
            showErrorToast(error, "integrations");
            setConnectingIntegrations((prev) => {
              const next = new Set(prev);
              next.delete(InstalledIntegrationType.PARAGON_EQUISOFT);
              return next;
            });
          },
        });
      } catch (error) {
        console.error("Paragon Equisoft installation error:", error);
        setConnectingIntegrations((prev) => {
          const next = new Set(prev);
          next.delete(InstalledIntegrationType.PARAGON_EQUISOFT);
          return next;
        });
      }
    };

  // Detects the user dismissing the OAuth popup without finishing (Paragon
  // fires no reliable close event for a bare popup — Gmail, headless Outlook,
  // headless Maximizer).
  // installIntegration doesn't return the popup handle, so we briefly wrap
  // window.open to grab the popup it opens synchronously, then poll
  // popup.closed and call onPopupClosed(). stop() (from onSuccess/onError)
  // halts polling so a completed connect never triggers a stray clear; if no
  // popup is captured, onError is the fallback.
  const watchForOAuthPopupCancel = (
    onPopupClosed: () => void,
    captureWindowMs = 0,
  ) => {
    let stopped = false;
    let popup: Window | null = null;
    let graceTimer: number | undefined;
    const originalOpen = window.open;
    const wrappedOpen = ((...args: Parameters<typeof window.open>) => {
      popup = originalOpen.apply(window, args);
      return popup;
    }) as typeof window.open;
    window.open = wrappedOpen;
    // Only restore if our wrapper is still the active override, so a newer
    // concurrent watcher's wrapper isn't clobbered by out-of-order cleanup.
    const restoreOpen = () => {
      if (window.open === wrappedOpen) window.open = originalOpen;
    };
    // Restore after the current tick by default; the popup opens synchronously,
    // so it's already captured by then. Callers whose popup opens a few ticks in
    // (installFlow) pass a longer capture window.
    window.setTimeout(restoreOpen, captureWindowMs);

    const interval = window.setInterval(() => {
      if (stopped || !popup) {
        if (stopped) window.clearInterval(interval);
        return;
      }
      if (popup.closed) {
        window.clearInterval(interval);
        // Don't treat the close as a cancel immediately — a successful connect
        // closes the popup too, then the SDK polls a few seconds for the
        // credential before onSuccess fires. Wait out the grace period; if the
        // flow succeeds/errors in the meantime, stop() clears this timer.
        graceTimer = window.setTimeout(() => {
          if (!stopped) onPopupClosed();
        }, OAUTH_POPUP_CANCEL_GRACE_MS);
      }
    }, 500);
    // Safety net so the interval can't leak if the popup is never captured.
    const maxLifetime = window.setTimeout(
      () => window.clearInterval(interval),
      5 * 60 * 1000,
    );

    return () => {
      stopped = true;
      restoreOpen();
      window.clearInterval(interval);
      window.clearTimeout(maxLifetime);
      if (graceTimer !== undefined) window.clearTimeout(graceTimer);
    };
  };

  const handleParagonGoogleIntegrationInstall: () => Promise<void> = async () => {
    if (!paragonJwt || !paragonUser) {
      console.error("Paragon not authenticated yet");
      return;
    }
    setConnectingIntegrations((prev) =>
      new Set(prev).add(InstalledIntegrationType.PARAGON_GOOGLE),
    );
    const attemptId =
      (connectAttemptRef.current[InstalledIntegrationType.PARAGON_GOOGLE] ?? 0) +
      1;
    connectAttemptRef.current[InstalledIntegrationType.PARAGON_GOOGLE] =
      attemptId;
    let stopCancelWatch = () => {};
    const clearConnecting = () => {
      stopCancelWatch();
      // Superseded by a newer attempt — don't wipe its spinner.
      if (
        connectAttemptRef.current[InstalledIntegrationType.PARAGON_GOOGLE] !==
        attemptId
      ) {
        return;
      }
      setConnectingIntegrations((prev) => {
        const next = new Set(prev);
        next.delete(InstalledIntegrationType.PARAGON_GOOGLE);
        return next;
      });
    };
    stopCancelWatch = watchForOAuthPopupCancel(clearConnecting);
    try {
      paragon.installIntegration("gmail", {
        showPortalAfterInstall: false,
        allowMultipleCredentials: true,
        async onSuccess(event) {
          // Stop the poll so it can't flip back to "Add" mid-persist.
          stopCancelWatch();
          try {
            await createIntegration({
              integration: InstalledIntegrationType.PARAGON_GOOGLE,
              externalId: event.credentialId ?? "",
            });

            await queryClient.invalidateQueries({
              queryKey: [InstalledIntegration_ALL_GET],
            });
            toast.success("Gmail has been successfully connected.");
          } catch (error) {
            console.error("Failed to save Gmail integration:", error);
            showErrorToast(error, "integrations");
            try {
              await paragon.uninstallIntegration("gmail", {
                selectedCredentialId: event.credentialId,
              });
            } catch (rollbackError) {
              console.warn(
                "Failed to rollback Gmail Paragon credential:",
                rollbackError,
              );
            }
          } finally {
            clearConnecting();
          }
        },
        onError(error) {
          console.error("gmail connect error", error);
          showErrorToast(error, "integrations");
          clearConnecting();
        },
      });
    } catch (error) {
      console.error("Paragon Gmail installation error:", error);
      clearConnecting();
    }
  };

  const handleParagonMicrosoftIntegrationInstall: () => Promise<void> = async () => {
    if (!paragonJwt || !paragonUser) {
      console.error("Paragon not authenticated yet");
      return;
    }
    setConnectingIntegrations((prev) =>
      new Set(prev).add(InstalledIntegrationType.PARAGON_MICROSOFT),
    );
    const attemptId =
      (connectAttemptRef.current[InstalledIntegrationType.PARAGON_MICROSOFT] ??
        0) + 1;
    connectAttemptRef.current[InstalledIntegrationType.PARAGON_MICROSOFT] =
      attemptId;
    let stopCancelWatch = () => {};
    const clearConnecting = () => {
      stopCancelWatch();
      // Superseded by a newer attempt — don't wipe its spinner.
      if (
        connectAttemptRef.current[InstalledIntegrationType.PARAGON_MICROSOFT] !==
        attemptId
      ) {
        return;
      }
      setConnectingIntegrations((prev) => {
        const next = new Set(prev);
        next.delete(InstalledIntegrationType.PARAGON_MICROSOFT);
        return next;
      });
    };
    stopCancelWatch = watchForOAuthPopupCancel(clearConnecting);
    try {
      // Outlook (unlike Gmail) carries an optional "Entra Domain or Tenant ID"
      // pre-oauth input, which makes the SDK route installIntegration through the
      // Connect Portal iframe — whose window.open is outside the user gesture, so
      // Chrome blocks the popup. setHeadless(true) short-circuits that routing so
      // the popup opens in the top window (in-gesture, unblocked). The tenant
      // field is optional and Microsoft resolves work/personal at its own login,
      // so going headless is a no-op for the user. Reset on every exit path.
      paragon.setHeadless(true);
      paragon.installIntegration("outlook", {
        showPortalAfterInstall: false,
        allowMultipleCredentials: true,
        async onSuccess(event) {
          paragon.setHeadless(false);
          // Stop the poll so it can't flip back to "Add" mid-persist.
          stopCancelWatch();
          try {
            await createIntegration({
              integration: InstalledIntegrationType.PARAGON_MICROSOFT,
              externalId: event.credentialId ?? "",
            });

            await queryClient.invalidateQueries({
              queryKey: [InstalledIntegration_ALL_GET],
            });
            toast.success("Outlook has been successfully connected.");
          } catch (error) {
            console.error("Failed to save Outlook integration:", error);
            showErrorToast(error, "integrations");
            try {
              await paragon.uninstallIntegration("outlook", {
                selectedCredentialId: event.credentialId,
              });
            } catch (rollbackError) {
              console.warn(
                "Failed to rollback Outlook Paragon credential:",
                rollbackError,
              );
            }
          } finally {
            clearConnecting();
          }
        },
        onError(error) {
          paragon.setHeadless(false);
          console.error("outlook connect error", error);
          showErrorToast(error, "integrations");
          clearConnecting();
        },
      });
    } catch (error) {
      paragon.setHeadless(false);
      console.error("Paragon Outlook installation error:", error);
      clearConnecting();
    }
  };

  const handleParagonMicrosoftDynamicsIntegrationInstall: () => Promise<void> =
    async () => {
      if (!paragonJwt || !paragonUser) {
        console.error("Paragon not authenticated yet");
        return;
      }
      setConnectingIntegrations((prev) =>
        new Set(prev).add(InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS),
      );
      try {
        const microsoftConfig = paragonConfigs?.find(
          (config) =>
            config.integration ===
            InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
        );
        const firmConfig =
          microsoftConfig?.configuration.allConfigurations?.find(
            (c: ParagonConfiguration) =>
              c.connectCredentialId === microsoftConfig.connectionId,
          );
        if (!firmConfig) {
          console.error("Microsoft Dynamics configuration not found");
          setConnectingIntegrations((prev) => {
            const next = new Set(prev);
            next.delete(InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS);
            return next;
          });
          return;
        }
        paragon.installIntegration("microsoftDynamics", {
          showPortalAfterInstall: false,
          allowMultipleCredentials: true,
          async onSuccess(event) {
            try {
              // Only save the user's credential, don't update firm-level config
              await createIntegration({
                integration: InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
                externalId: event.credentialId ?? "",
              });
              /*
               * This will ONLY copy over workflow settings from the firm-level config to the user-level config in Paragon.
               * Future user setting changes in the admin will not be reflected in Paragon for this user.
               */
              paragon.updateIntegrationUserSettings("microsoftDynamics", {
                ...firmConfig?.sharedSettings,
              });

              await queryClient.invalidateQueries({
                queryKey: [InstalledIntegration_ALL_GET],
              });
              toast.success(
                "Microsoft Dynamics has been successfully connected.",
              );
            } catch (error) {
              console.error(
                "Failed to save Microsoft Dynamics integration:",
                error,
              );
              showErrorToast(error, "integrations");
              // Rollback the Paragon credential to prevent state mismatch
              try {
                await paragon.uninstallIntegration("microsoftDynamics", {
                  selectedCredentialId: event.credentialId,
                });
              } catch (rollbackError) {
                console.warn(
                  "Failed to rollback Microsoft Dynamics Paragon credential:",
                  rollbackError,
                );
              }
            } finally {
              setConnectingIntegrations((prev) => {
                const next = new Set(prev);
                next.delete(InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS);
                return next;
              });
            }
          },
          onError(error) {
            console.error("microsoft dynamics connect error", error);
            showErrorToast(error, "integrations");
            setConnectingIntegrations((prev) => {
              const next = new Set(prev);
              next.delete(InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS);
              return next;
            });
          },
        });
      } catch (error) {
        console.error("Paragon MS Dynamics installation error:", error);
        setConnectingIntegrations((prev) => {
          const next = new Set(prev);
          next.delete(InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS);
          return next;
        });
      }
    };

  // Helper function to get display name for Salesforce skin integrations
  const getSalesforceSkinDisplayName = (
    integrationType:
      | InstalledIntegrationType.PARAGON_SALESFORCE
      | InstalledIntegrationType.PARAGON_XLR8
      | InstalledIntegrationType.PARAGON_PRACTIFI
      | InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS
      | InstalledIntegrationType.PARAGON_FSC,
  ): string => {
    switch (integrationType) {
      case InstalledIntegrationType.PARAGON_SALESFORCE:
        return "Salesforce";
      case InstalledIntegrationType.PARAGON_XLR8:
        return "XLR8";
      case InstalledIntegrationType.PARAGON_PRACTIFI:
        return "Practifi";
      case InstalledIntegrationType.PARAGON_FSC:
        return "Salesforce FSC";
      case InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS:
        return "SS&C Black Diamond Elements";
    }
  };

  const handleParagonSalesforceSkinIntegrationInstall = useCallback(
    async (
      integrationType:
        | InstalledIntegrationType.PARAGON_SALESFORCE
        | InstalledIntegrationType.PARAGON_XLR8
        | InstalledIntegrationType.PARAGON_PRACTIFI
        | InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS
        | InstalledIntegrationType.PARAGON_FSC,
    ): Promise<void> => {
      if (!paragonJwt || !paragonUser) {
        console.error("Paragon not authenticated yet");
        return;
      }
      setConnectingIntegrations((prev) => new Set(prev).add(integrationType));
      try {
        const firmConfigurationId: string | null =
          getParagonFirmConfigurationId(integrationType);
        if (!firmConfigurationId) {
          console.error("Salesforce configuration not found", {
            integrationType,
          });
          setConnectingIntegrations((prev) => {
            const next = new Set(prev);
            next.delete(integrationType);
            return next;
          });
          return;
        }

        const userInstalledIntegration: InstalledIntegration | undefined =
          installedIntegrations?.find((i) => i.integration === integrationType);
        const hasUserLevelIntegration: boolean =
          userInstalledIntegration?.externalId != undefined;
        const hasFirmLevelSdkCredential: boolean =
          paragonUser.integrations?.salesforce?.credentialId != undefined ||
          (Array.isArray(
            paragonUser.integrations?.salesforce?.allCredentials,
          ) &&
            paragonUser.integrations.salesforce.allCredentials.length > 0);

        // If Paragon SDK shows a firm-level Salesforce connection but no user-level integration exists,
        // uninstall first so installIntegration doesn't error with "already installed".
        if (!hasUserLevelIntegration && hasFirmLevelSdkCredential) {
          try {
            await paragon.uninstallIntegration("salesforce");
            // Wait a bit for the SDK to update its state
            await new Promise((resolve) => setTimeout(resolve, 100));
          } catch (error) {
            console.warn(
              "Failed to uninstall firm-level Salesforce connection from SDK:",
              error,
            );
          }
        }
        const salesforceConfig = paragonConfigs?.find(
          (config) => config.integration === integrationType,
        );
        const firmConfig =
          salesforceConfig?.configuration.allConfigurations?.find(
            (c: ParagonConfiguration) =>
              c.connectCredentialId === salesforceConfig.connectionId,
          );
        if (!firmConfig) {
          console.error("Salesforce configuration not found");
          setConnectingIntegrations((prev) => {
            const next = new Set(prev);
            next.delete(integrationType);
            return next;
          });
          return;
        }
        paragon.installIntegration("salesforce", {
          showPortalAfterInstall: false,
          async onSuccess(event) {
            try {
              await createIntegration({
                integration: integrationType,
                externalId: event.credentialId ?? "",
              });
              /*
               * This will ONLY copy over workflow settings from the firm-level config to the user-level config in Paragon.
               * Future user setting changes in the admin will not be reflected in Paragon for this user.
               */
              paragon.updateIntegrationUserSettings("salesforce", {
                ...firmConfig?.sharedSettings,
              });

              await queryClient.invalidateQueries({
                queryKey: [InstalledIntegration_ALL_GET],
              });
              toast.success(
                `${getSalesforceSkinDisplayName(integrationType)} has been successfully connected.`,
              );
            } catch (error) {
              console.error("Failed to save Salesforce integration:", error);
              showErrorToast(error, "integrations");
              // Rollback the Paragon credential to prevent state mismatch
              try {
                await paragon.uninstallIntegration("salesforce", {
                  selectedCredentialId: event.credentialId,
                });
              } catch (rollbackError) {
                console.warn(
                  "Failed to rollback Salesforce Paragon credential:",
                  rollbackError,
                );
              }
            } finally {
              setConnectingIntegrations((prev) => {
                const next = new Set(prev);
                next.delete(integrationType);
                return next;
              });
            }
          },
          onError(error) {
            console.error("salesforce connect error", error);
            showErrorToast(error, "integrations");
            setConnectingIntegrations((prev) => {
              const next = new Set(prev);
              next.delete(integrationType);
              return next;
            });
          },
        });
      } catch (error) {
        console.error("Paragon Salesforce installation error:", error);
        setConnectingIntegrations((prev) => {
          const next = new Set(prev);
          next.delete(integrationType);
          return next;
        });
      }
    },
    [
      getParagonFirmConfigurationId,
      installedIntegrations,
      paragonJwt,
      paragonUser,
      queryClient,
      paragonConfigs,
    ],
  );

  // Integration finding logic
  const {
    google,
    microsoft,
    zoom,
    salesforce,
    wealthbox,
    redtail,
    laylah,
    paragonSalesforce,
    paragonXlr8,
    paragonPractifi,
    paragonBlackDiamondElements,
    paragonFsc,
    paragonMicrosoftDynamics,
    paragonZoho,
    paragonCloven,
    paragonMaximizer,
    paragonEquisoft,
    paragonGoogle,
    paragonMicrosoft,
  } = useMemo(() => {
    const google = installedIntegrations?.filter(
      (c) => c.integration === InstalledIntegrationType.GOOGLECALENDAR,
    );
    const microsoft = installedIntegrations?.filter(
      (c) => c.integration === InstalledIntegrationType.MICROSOFTCALENDAR,
    );
    const zoom = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.ZOOM,
    );
    const salesforce = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.SALESFORCE,
    );
    const wealthbox = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.WEALTHBOX,
    );
    const redtail = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.REDTAIL,
    );
    const laylah = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.LAYLAH,
    );
    const paragonSalesforce = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_SALESFORCE,
    );
    const paragonXlr8 = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_XLR8,
    );
    const paragonPractifi = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_PRACTIFI,
    );
    const paragonBlackDiamondElements = installedIntegrations?.find(
      (i) =>
        i.integration ===
        InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS,
    );
    const paragonFsc = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_FSC,
    );
    const paragonMicrosoftDynamics = installedIntegrations?.find(
      (i) =>
        i.integration === InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
    );
    const paragonZoho = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_ZOHO,
    );
    const paragonCloven = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_CLOVEN,
    );
    const paragonMaximizer = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_MAXIMIZER,
    );
    const paragonEquisoft = installedIntegrations?.find(
      (i) => i.integration === InstalledIntegrationType.PARAGON_EQUISOFT,
    );
    const paragonGoogle = installedIntegrations?.filter(
      (i) => i.integration === InstalledIntegrationType.PARAGON_GOOGLE,
    );
    const paragonMicrosoft = installedIntegrations?.filter(
      (i) => i.integration === InstalledIntegrationType.PARAGON_MICROSOFT,
    );
    return {
      google,
      microsoft,
      zoom,
      salesforce,
      wealthbox,
      redtail,
      laylah,
      paragonSalesforce,
      paragonXlr8,
      paragonPractifi,
      paragonBlackDiamondElements,
      paragonFsc,
      paragonMicrosoftDynamics,
      paragonZoho,
      paragonCloven,
      paragonMaximizer,
      paragonEquisoft,
      paragonGoogle,
      paragonMicrosoft,
    };
  }, [installedIntegrations]);

  // Calendars share the same connection-state gate as installed integrations.
  const { data: calendars } = useCalendars({
    refetchInterval: integrationsPollInterval,
  });

  const isAnyCalendarConnected = useMemo(() => {
    return !!google?.length || !!microsoft?.length;
  }, [google, microsoft]);

  const isCalendarConnected = useMemo(
    () =>
      !!installedIntegrations?.length &&
      installedIntegrations.some((i) =>
        [
          InstalledIntegrationType.GOOGLECALENDAR,
          InstalledIntegrationType.MICROSOFTCALENDAR,
        ].includes(i.integration),
      ),
    [installedIntegrations],
  );

  // CRM logic
  const srmsList = [salesforce, redtail, wealthbox, laylah].filter(Boolean);

  const onConnectToCrm = (type: CrmType) => {
    setConnectingIntegrations((prev) => new Set(prev).add(type));
    if (
      srmsList.length &&
      srmsList.some(
        (crmIntegration) => crmIntegration?.integration !== type.toUpperCase(),
      )
    ) {
      setWarningModal(type);
    } else {
      setActiveCrm(type);
    }
  };

  const disconnectOtherCrms = (type: CrmType | null) => {
    if (!type) return;
    srmsList.forEach((crmIntegration) => {
      if (
        crmIntegration?.id &&
        crmIntegration?.integration !== type.toUpperCase()
      ) {
        onDisconnect(crmIntegration.id, () => setActiveCrm(warningModal));
      }
    });

    setWarningModal(null);
  };

  // OAuth handlers
  const onGCalClick = useCallback(() => {
    if (!userId) return;
    const url = buildGoogleCalendarOAuthUrl({
      userid: userId,
    });
    window.location.href = url;
  }, [userId]);

  const onOutlookClick = useCallback(() => {
    if (!userId) return;
    const url = buildMicrosoftOutlookOAuthUrl({
      userid: userId,
    });
    window.location.href = url;
  }, [userId]);

  const onZoomClick = useCallback(() => {
    const url = buildZoomOAuthUrl();
    window.location.href = url;
  }, []);

  const onSalesforceClick = useCallback((loginUrl?: string) => {
    const url = buildSalesforceOAuthUrl(loginUrl);
    window.open(url, "_blank");
  }, []);

  const onDisconnect = useCallback(
    (id: string, successCallback?: () => void) => {
      disconnect({ id, successCallback });
    },
    [disconnect],
  );

  // Handler to run before disconnecting - handles Paragon uninstallation for Paragon integrations
  const handleBeforeDisconnect = useCallback(
    async (id: string) => {
      // Uninstalling from the Paragon SDK is best-effort defense-in-depth: the
      // authoritative credential removal happens server-side during the backend
      // disconnect (DELETE /installedintegration/:id). All accounts are
      // multi-account safe, so the credential is targeted via
      // { selectedCredentialId } — passing a bare credential string is ignored
      // by the SDK and silently fails to disconnect. A failure here must never
      // block the backend removal, so it is awaited and swallowed.
      try {
        // Check if this is the Zoho Paragon integration
        if (paragonZoho?.id === id && paragonZoho?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("zohocrm", {
              selectedCredentialId: paragonZoho.externalId,
            }),
          );
        }
        // Check if this is the Cloven Paragon integration (uses zohocrm SDK key)
        if (paragonCloven?.id === id && paragonCloven?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("zohocrm", {
              selectedCredentialId: paragonCloven.externalId,
            }),
          );
        }
        // Check if this is the Microsoft Dynamics Paragon integration
        if (
          paragonMicrosoftDynamics?.id === id &&
          paragonMicrosoftDynamics?.externalId
        ) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("microsoftDynamics", {
              selectedCredentialId: paragonMicrosoftDynamics.externalId,
            }),
          );
        }
        // Check if this is the Salesforce Paragon integration
        if (paragonSalesforce?.id === id && paragonSalesforce?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("salesforce", {
              selectedCredentialId: paragonSalesforce.externalId,
            }),
          );
        }
        if (paragonXlr8?.id === id && paragonXlr8?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("salesforce", {
              selectedCredentialId: paragonXlr8.externalId,
            }),
          );
        }
        if (paragonPractifi?.id === id && paragonPractifi?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("salesforce", {
              selectedCredentialId: paragonPractifi.externalId,
            }),
          );
        }
        if (
          paragonBlackDiamondElements?.id === id &&
          paragonBlackDiamondElements?.externalId
        ) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("salesforce", {
              selectedCredentialId: paragonBlackDiamondElements.externalId,
            }),
          );
        }
        if (paragonFsc?.id === id && paragonFsc?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("salesforce", {
              selectedCredentialId: paragonFsc.externalId,
            }),
          );
        }
        // Check if this is the Maximizer Paragon integration
        if (paragonMaximizer?.id === id && paragonMaximizer?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("custom.maximizer", {
              selectedCredentialId: paragonMaximizer.externalId,
            }),
          );
        }
        // Check if this is the Equisoft Paragon integration
        if (paragonEquisoft?.id === id && paragonEquisoft?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("custom.equisoft", {
              selectedCredentialId: paragonEquisoft.externalId,
            }),
          );
        }
        // Check if this is a Gmail Paragon integration row (multi-account safe)
        const googleRow = paragonGoogle?.find((g) => g.id === id);
        if (googleRow?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("gmail", {
              selectedCredentialId: googleRow.externalId,
            }),
          );
        }
        // Check if this is an Outlook Paragon integration row (multi-account safe)
        const microsoftRow = paragonMicrosoft?.find((m) => m.id === id);
        if (microsoftRow?.externalId) {
          await withUninstallTimeout(
            paragon.uninstallIntegration("outlook", {
              selectedCredentialId: microsoftRow.externalId,
            }),
          );
        }
      } catch (error) {
        // Backend disconnect is authoritative; log and continue.
        console.error(
          "Paragon SDK uninstallIntegration failed; backend will still remove the credential",
          error,
        );
      }
    },
    [
      paragonBlackDiamondElements,
      paragonCloven,
      paragonEquisoft,
      paragonFsc,
      paragonMaximizer,
      paragonMicrosoftDynamics,
      paragonPractifi,
      paragonSalesforce,
      paragonXlr8,
      paragonZoho,
      paragonGoogle,
      paragonMicrosoft,
    ],
  );

  const onConnect = useCallback(
    (platform: Integrations) => {
      switch (platform) {
        case Integrations.GOOGLE:
          onGCalClick();
          break;
        case Integrations.MICROSOFT:
          onOutlookClick();
          break;
        case Integrations.ZOOM:
          onZoomClick();
          break;
        case Integrations.SALESFORCE:
          onSalesforceClick();
          break;
        default:
          break;
      }
    },
    [onGCalClick, onOutlookClick, onZoomClick, onSalesforceClick],
  );

  // Integration states logic
  const integrationStates = useMemo(() => {
    return {
      teams: {
        isConnected: false,
        isEnabled: isAnyCalendarConnected,
        onConnect: isAnyCalendarConnected
          ? undefined
          : () => onConnect(Integrations.MICROSOFT),
        onDisconnect: undefined,
      },
      meet: {
        isConnected: false,
        isEnabled: isAnyCalendarConnected,
        onConnect: isAnyCalendarConnected
          ? undefined
          : () => onConnect(Integrations.GOOGLE),
        onDisconnect: undefined,
      },
      zoom: {
        isConnected: false,
        isEnabled: isAnyCalendarConnected,
        onConnect: undefined,
        onDisconnect: undefined,
      },
    };
  }, [isAnyCalendarConnected, onConnect]);

  const isParagonEmailConfigured = useCallback(
    (
      integrationType:
        | InstalledIntegrationType.PARAGON_GOOGLE
        | InstalledIntegrationType.PARAGON_MICROSOFT,
    ): boolean =>
      paragonConfigs?.some(
        (config) =>
          config.integration === integrationType &&
          config.connectionId != undefined,
      ) ?? false,
    [paragonConfigs],
  );

  const paragonGoogleEmailConfigured = isParagonEmailConfigured(
    InstalledIntegrationType.PARAGON_GOOGLE,
  );
  const paragonMicrosoftEmailConfigured = isParagonEmailConfigured(
    InstalledIntegrationType.PARAGON_MICROSOFT,
  );

  const paragonGoogleAccounts = useMemo(
    () =>
      (paragonGoogle ?? []).map((i) => ({
        id: i.id,
        email: i.username ?? null,
        status: i.status,
      })),
    [paragonGoogle],
  );

  const paragonMicrosoftAccounts = useMemo(
    () =>
      (paragonMicrosoft ?? []).map((i) => ({
        id: i.id,
        email: i.username ?? null,
        status: i.status,
      })),
    [paragonMicrosoft],
  );

  const handleParagonEmailDisconnect = useCallback(
    async (id: string) => {
      // Re-entry guard so a second click can't fire a duplicate delete.
      if (disconnectingIdsRef.current.has(id)) return;
      disconnectingIdsRef.current.add(id);
      setDisconnectingIds(new Set(disconnectingIdsRef.current));
      // handleBeforeDisconnect swallows its errors, so onDisconnect always runs
      // and onSettled clears the flag on every path.
      await handleBeforeDisconnect(id);
      onDisconnect(id);
    },
    [handleBeforeDisconnect, onDisconnect],
  );

  return {
    connect: onConnect,
    disconnect: onDisconnect,
    installedIntegrations: installedIntegrations?.length
      ? installedIntegrations
      : undefined,
    isFetched,
    isCalendarConnected,
    isIntegrationsLoading: isFetching,
    // Integration finding
    google,
    microsoft,
    calendars,
    zoom,
    salesforce,
    wealthbox,
    redtail,
    laylah,
    isLaylahFirmConfigured,
    isCrmConfigurationsLoading,
    isCrmConfigurationsError,
    refetchCrmConfigurations,
    isAnyCalendarConnected,
    // Integration states
    integrationStates,
    paragonSalesforceEnabled,
    isParagonJwtLoading,
    isParagonUserSet: !!paragonUser,
    paragonAuthState,
    paragonConfigsState,
    retryParagonAuth,
    paragonConfigs,
    paragonSalesforce,
    paragonXlr8,
    paragonPractifi,
    paragonBlackDiamondElements,
    paragonFsc,
    paragonMicrosoftDynamics,
    paragonZoho,
    paragonCloven,
    paragonMaximizer,
    paragonEquisoft,
    paragonGoogle,
    paragonMicrosoft,
    paragonGoogleEmailConfigured,
    paragonMicrosoftEmailConfigured,
    paragonGoogleAccounts,
    paragonMicrosoftAccounts,
    handleParagonEmailDisconnect,
    paragonSyncStatus,
    handleParagonMicrosoftDynamicsIntegrationInstall,
    handleParagonZohoIntegrationInstall,
    handleParagonClovenIntegrationInstall,
    handleParagonMaximizerIntegrationInstall,
    handleParagonEquisoftIntegrationInstall,
    handleParagonGoogleIntegrationInstall,
    handleParagonMicrosoftIntegrationInstall,
    handleParagonSalesforceSkinIntegrationInstall,
    // CRM logic
    onConnectToCrm,
    disconnectOtherCrms,
    srmsList,
    // Modal state
    integrationToDisconnect,
    setIntegrationToDisconnect,
    handleBeforeDisconnect,
    activeCrm,
    setActiveCrm,
    warningModal,
    setWarningModal,
    isFetching,
    connectingIntegrations,
    disconnectingIds,
    setConnectingIntegrations,
  };
};
